import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CaretLeft } from "@phosphor-icons/react";

import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page, statusColor } from "@/components/lims";
import BoxGrid, { WellPreview, boxCapacity, occupancyMap } from "@/components/BoxGrid";
import RenameField from "@/components/RenameField";
import { NotFoundState } from "@/pages/NotFound";
import { canManageStorage } from "@/lib/roles";

/**
 * One box, as a page rather than a modal.
 *
 * The grid used to open in a dialog, so it could not be linked to, did not
 * survive a refresh, and closed if you clicked past it. At a bench terminal
 * that is the difference between sending someone a position and describing it
 * over the phone. The selected well lives in the query string for the same
 * reason: `?position=C5` is a link to a shelf.
 */
export default function BoxWorkspace() {
  const { id } = useParams();
  const { user } = useAuth();
  const canManage = canManageStorage(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const [box, setBox] = useState(null);
  const [path, setPath] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const selected = searchParams.get("position");
  const select = (position) => {
    const next = new URLSearchParams(searchParams);
    if (position) next.set("position", position);
    else next.delete("position");
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/boxes/${id}`);
        if (cancelled) return;
        setBox(data);
        setError(null);
        // The box knows its rack, not the freezer above it, so the tree supplies
        // the breadcrumb rather than a second lookup per level.
        try {
          const { data: freezers } = await api.get("/freezers");
          freezers.forEach((freezer) => freezer.racks?.forEach((rack) => {
            if (rack.boxes?.some((b) => b.id === id)) {
              setPath({ freezer: freezer.name, rack: rack.name });
            }
          }));
        } catch {
          // A missing breadcrumb is not worth failing the page over.
        }
      } catch (err) {
        if (!cancelled) {
          setError({ status: err.response?.status, message: err.message });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);

  const samples = useMemo(
    () => [...(box?.samples || [])].sort((a, b) => (a.position || "").localeCompare(b.position || "")),
    [box],
  );

  const renameBox = async (name) => {
    try {
      const { data } = await api.patch(`/boxes/${id}`, { name });
      setBox((current) => (current ? { ...current, name: data.name } : current));
      toast.success(`Renamed to ${data.name}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      throw err;
    }
  };

  if (loading) {
    return (
      <Page>
        <div className="h-72 animate-pulse rounded-lg border border-line bg-panel" />
      </Page>
    );
  }

  if (error?.status === 404) {
    return (
      <Page>
        <NotFoundState
          title="Storage box not found"
          message="This box may have been retired, or you may not have access to its storage location."
          homeTo="/storage"
          homeLabel="View storage"
        />
      </Page>
    );
  }

  if (error || !box) {
    return (
      <Page>
        <div role="alert" className="rounded-md border border-status-critical/30 bg-status-critical/10 p-4 text-sm text-status-critical">
          <p>{error?.message || "The storage box could not be loaded."}</p>
          <button type="button" onClick={() => window.location.reload()} className="mt-3 font-medium underline underline-offset-2">
            Try again
          </button>
        </div>
      </Page>
    );
  }

  const occupied = Object.keys(occupancyMap(box.samples)).length;
  const capacity = boxCapacity(box);

  return (
    <Page>
      <Link
        to="/storage"
        className="mb-4 inline-flex items-center gap-1 text-sm text-meta hover:text-ink"
      >
        <CaretLeft size={14} /> Storage
      </Link>

      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          {/* The box is renamed here rather than on the storage map, where its
              card is one big link and a button inside a link is not valid
              markup. This is also the page you are already on when you notice
              the name is wrong. */}
          <RenameField
            value={box.name}
            label={`box ${box.name}`}
            testid="rename-box"
            onRename={canManage ? renameBox : null}
          >
            <h1 className="truncate font-display text-3xl font-semibold tracking-tight text-ink">
              {box.name}
            </h1>
          </RenameField>
          <p className="mt-1 text-sm text-meta">
            {path ? `${path.freezer} / ${path.rack}` : "Storage"} · {occupied} of {capacity} positions used
          </p>
        </div>
        <div className="w-56">
          {/* On a white card the surface token is nearly invisible, which left
              the fill floating with nothing to measure it against. */}
          <div className="h-1.5 overflow-hidden rounded-full bg-line" aria-hidden="true">
            <div
              className="h-full rounded-full bg-teal-600"
              style={{ width: `${capacity ? (occupied / capacity) * 100 : 0}%` }}
            />
          </div>
          <p className="mt-1.5 text-right text-xs text-meta" aria-hidden="true">
            {capacity - occupied} free
          </p>
        </div>
      </div>

      {/* `items-start` is what keeps the grid card honest: as a stretched grid
          item it grew to whatever the column beside it needed, so a 9×9 box
          sat in a card half of which was empty. Each card now ends where its
          content does, and the box contents move below rather than lengthening
          the row. */}
      <div className="grid items-start gap-6 lg:grid-cols-[auto_minmax(18rem,22rem)]">
        <div className="rounded-lg border border-line bg-panel p-5">
          <BoxGrid box={box} selected={selected} onSelect={select} />
          <p className="mt-4 text-xs text-meta">
            Wells are colored by sample status. Select one to see what is stored there.
          </p>
        </div>

        <WellPreview box={box} position={selected} onClear={() => select(null)} />
      </div>

      <div className="mt-6 rounded-lg border border-line">
        <div className="border-b border-line px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-meta">
          Stored here ({samples.length})
        </div>
        {samples.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-meta">This box is empty.</p>
        ) : (
          /* Full width now, so the list runs in columns instead of one long
             scroll: a 15-sample box fits without scrolling at all, and an
             81-position one still bounds its height. */
          <ul className="grid max-h-72 gap-x-4 overflow-auto p-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {samples.map((sample) => (
              <li key={sample.id}>
                {/* Selecting from the list moves the grid, and selecting on
                    the grid highlights the row: one selection, two views. */}
                <button
                  type="button"
                  data-testid={`stored-${sample.sample_code}`}
                  onClick={() => select(sample.position)}
                  className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-surface ${
                    selected === sample.position ? "bg-teal-50" : ""
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: statusColor(sample.status) }}
                  />
                  <span className="w-9 shrink-0 font-mono text-xs text-meta">{sample.position}</span>
                  <span className="min-w-0 flex-1 truncate text-sm text-ink">{sample.sample_code}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Page>
  );
}
